import apiService from '../Api'
import { Teacher, TeacherComplete, TeacherCreateResponse, KnowledgeArea, TeachingFormat, SelectTeacher } from './types'

export interface ITeacherFields {
  name: string
  email: string | null
  phone: string | null
  banner_picture: File | null
  profile_picture: File | null
  iam_picture: File | null
  highlight_phrase: string
  iam_text: string
  staff_body_text: string
  teaching_formats: TeachingFormat[]
  knowledge_areas: number[]
  enabled: boolean
  show_on_forms: boolean
}

export class TeacherService {
  async list(queryParams: {
    search: string
    length: number
    page: number
    status?: string
  }): Promise<{ data: Teacher[]; total_filtered: number; total: number }> {
    const params = new URLSearchParams()
    params.append('search', queryParams.search)
    params.append('page_size', queryParams.length.toString())
    params.append('page', queryParams.page.toString())

    if (queryParams.status) {
      params.append('status', queryParams.status)
    }

    return apiService.get('/teachers', { params }).then((response) => response.data)
  }

  async get(id: number): Promise<TeacherComplete> {
    return apiService.get(`/teachers/${id}`).then((response) => response.data)
  }

  async delete(id: number): Promise<void> {
    return apiService.delete(`/teachers/${id}`)
  }

  async toggleStatus(id: number): Promise<void> {
    return apiService.patch(`/teachers/${id}/toggle-status`)
  }

  async updateShowOnForms(id: number, show_on_forms: boolean): Promise<void> {
    return apiService.patch(`/teachers/${id}/show-on-forms`, { show_on_forms })
  }

  async getKnowledgeAreas(): Promise<KnowledgeArea[]> {
    return apiService.get('/knowledge-areas').then((response) => response.data.areas)
  }

  async create(form: ITeacherFields): Promise<TeacherCreateResponse> {
    const formData = new FormData()
    formData.append('name', form.name)

    if (form.banner_picture) {
      formData.append('banner_picture', form.banner_picture)
    }

    if (form.profile_picture) {
      formData.append('profile_picture', form.profile_picture)
    }

    if (form.iam_picture) {
      formData.append('iam_picture', form.iam_picture)
    }

    formData.append('email', form.email ?? '')
    formData.append('phone', (form.phone ?? '').replace(/\D/g, ''))

    formData.append('highlight_phrase', form.highlight_phrase)
    formData.append('iam_text', form.iam_text)
    formData.append('staff_body_text', form.staff_body_text)
    formData.append('enabled', form.enabled ? '1' : '0')
    formData.append('show_on_forms', form.show_on_forms ? '1' : '0')

    form.teaching_formats.forEach((format) => {
      formData.append('teaching_formats[]', format)
    })

    form.knowledge_areas.forEach((areaId) => {
      formData.append('knowledge_areas[]', areaId.toString())
    })

    // Desembrulha o corpo: a tela do professor lê o cupom criado junto com ele.
    return apiService
      .post('/teachers', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => response.data)
  }

  async update(id: number, form: ITeacherFields): Promise<TeacherComplete> {
    const formData = new FormData()
    formData.append('_method', 'PUT')
    formData.append('name', form.name)

    if (form.banner_picture) {
      formData.append('banner_picture', form.banner_picture)
    }

    if (form.profile_picture) {
      formData.append('profile_picture', form.profile_picture)
    }

    if (form.iam_picture) {
      formData.append('iam_picture', form.iam_picture)
    }

    formData.append('email', form.email ?? '')
    formData.append('phone', (form.phone ?? '').replace(/\D/g, ''))

    formData.append('highlight_phrase', form.highlight_phrase || '')
    formData.append('iam_text', form.iam_text || '')
    formData.append('staff_body_text', form.staff_body_text || '')
    formData.append('enabled', form.enabled ? '1' : '0')
    formData.append('show_on_forms', form.show_on_forms ? '1' : '0')

    form.teaching_formats?.forEach((format) => {
      formData.append('teaching_formats[]', format)
    })

    form.knowledge_areas?.forEach((areaId) => {
      formData.append('knowledge_areas[]', areaId.toString())
    })

    return apiService.post(`/teachers/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async listForSelect(): Promise<SelectTeacher[]> {
    return apiService.get('/teachers/select').then((response) => response.data)
  }

  /** Inclui os desativados: o relatório de NPS filtra respostas de professores que saíram do quadro. */
  async listForSelectIncludingDisabled(): Promise<SelectTeacher[]> {
    return apiService.get('/teachers/select', { params: { include_disabled: '1' } }).then((response) => response.data)
  }
}
